import { Column } from "primereact/column";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { useRef, useState, useMemo } from "react";

import { useSelector, useDispatch } from "react-redux";
import usePaginationStats from "src/hooks/usePaginationStats";
import {
  selectAssignedBuildings,
  setAssignedBuildingPopupData,
  retrieveBuildingInfoById,
  setNumberOfRowsPerPage,
  setGridCurrentPage,
  setIsContractPopupOpened,
  setIsSystemsPopupOpened,
  setFilterDataField,
  retrieveSystemServicesByBuilding,
  setIsUnassignBuildingPopupOpen,
} from "src/redux/slices/assignBuildingSlice";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { RoutePaths } from "src/resources/enum";
import GridPaginator from "src/components/GridPaginator/GridPaginator";
import i18n from "src/i18n";
import { Button } from "primereact/button";
import UnassignBuildingPopup from "./UnassignBuildingPopup";
import { toast } from "react-toastify";
import { isArabic } from "src/utils/helpers";
import { SortOrderDefault } from "src/resources/constant";

const AssignedBuildingsGrid = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const assignBuildingData = useSelector(selectAssignedBuildings);
  const { isUnAssignBuildingPopupOpen } = useSelector(
    (state: any) => state.assignBuilding.unassignedBuildingDetailsPopupData
  );
  const { t } = useTranslation(["common"]);
  const [sortOrder, setSortOrder] = useState<any | null>(SortOrderDefault);

  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [selectedPropertyRequestStatusId, setSelectedPropertyRequestStatusId] =
    useState(null);

  const [noOfActiveContracts, setNoOfActiveContracts] = useState(0);

  const { totalRowsCount, numberOfRowsPerPage, gridCurrentPage } = useSelector(
    (state: any) => state.assignBuilding
  );
  const { sortColumn } = useSelector(
    (state: any) => state.assignBuilding.filterData
  );
  const tableRef = useRef<DataTable<(typeof assignBuildingData)[0]>>(null);

  const caseIDTemplate = (rowData: any) => {
    return (
      <div onClick={() => handleBuildingNumberClick(rowData)}>
        {rowData.buildingNumber}
      </div>
    );
  };
  const handleBuildingNumberClick = (rowData: any) => {
    dispatch(retrieveBuildingInfoById(rowData.buildingNumber) as any).then(
      () => {
        // navigate to assigned building details page
        dispatch(setIsContractPopupOpened(false) as any);
        dispatch(setIsSystemsPopupOpened(false) as any);
        navigate(RoutePaths.AssignedBuildingDetails);
      }
    );
    dispatch(
      setAssignedBuildingPopupData({
        field: "buildingNumber",
        value: rowData.buildingNumber,
      })
    );
    dispatch(
      setAssignedBuildingPopupData({ field: "status", value: rowData.status })
    );
    dispatch(
      setAssignedBuildingPopupData({
        field: "statusReason",
        value: rowData.statusReason,
      })
    );
  };

  const onSort = (e: DataTableSortEvent) => {
    let sortFieldBEName = null;
    setSortOrder(e.sortOrder);
    dispatch(
      setFilterDataField({
        field: "sortColumn",
        value: sortFieldBEName ?? e.sortField,
      })
    );
    dispatch(
      setFilterDataField({
        field: "isAscending",
        value: e.sortOrder === 1,
      })
    );
    dispatch(setGridCurrentPage(0)); //to get latest data
  };

  const unassignBtnHandler = (
    propertyId: any,
    propertyRequestStatusId: any,
    activeContractCount: number
  ): void => {
    setSelectedPropertyId(propertyId);
    setNoOfActiveContracts(activeContractCount);
    setSelectedPropertyRequestStatusId(propertyRequestStatusId);
    getAndHandleBuildingServices(propertyId);
  };
  function getAndHandleBuildingServices(propertyId: string) {
    dispatch(retrieveSystemServicesByBuilding(propertyId) as any).then(
      (response: any) => {
        if (response.data.OperationResponse.OperationSucceeded) {
          dispatch(setIsUnassignBuildingPopupOpen(true) as any);
        } else {
          toast.error(
            isArabic()
              ? response.data.OperationResponse.ErrorDescriptionAr
              : response.data.OperationResponse.ErrorDescriptionEn
          );
        }
      }
    );
  }
  const unassignButtonTemplate = (rowData: any) => {
    return (
      <Button
        label={t("unassign")}
        onClick={() =>
          unassignBtnHandler(
            rowData.propertyId,
            rowData.propertyRequestStatusId,
            rowData.activeContractCount
          )
        }
        className=" d-flex gap-3 pay-invoice-button m-0"
        // disabled={rowData.activeContractCount === 0}
      ></Button>
    );
  };

  return (
    <div className="grid-outer-wrapper">
      <div className="grid-inner-wrapper">
        <DataTable
          ref={tableRef}
          lazy
          className="assign-building-grid"
          value={assignBuildingData}
          emptyMessage={t("emptyRecords")}
          sortField={sortColumn || undefined}
          sortOrder={sortOrder || null} // Ensure sortOrder is a number or null
          onSort={onSort}
          scrollable
        >
          <Column
            field="buildingNumber"
            header={t("propertyID")}
            body={caseIDTemplate}
            className="case-id-col lg-col"
          ></Column>
          <Column
            field="buildingName"
            header={t("buildingNameHeader")}
            sortable
            className="lg-col"
          ></Column>
          <Column
            field="activeContractCount"
            sortable
            header={t("activeContracts")}
            className="sm-col"
          ></Column>
          {
            <Column
              style={{
                borderRight:
                  i18n.language === "ar" ? "none" : "1px solid #e0e0e0",
                borderLeft:
                  i18n.language === "ar" ? "1px solid #e0e0e0" : "none",
              }}
              body={unassignButtonTemplate}
              className="sm-col"
            ></Column>
          }
        </DataTable>
      </div>
      <GridPaginator
        totalRowsCount={totalRowsCount}
        currentPage={gridCurrentPage}
        numberOfRowsPerPage={numberOfRowsPerPage}
        setNumberOfRowsPerPage={setNumberOfRowsPerPage}
        setGridCurrentPage={setGridCurrentPage}
        tableRef={tableRef}
      />
      {isUnAssignBuildingPopupOpen && (
        <UnassignBuildingPopup
          propertyId={selectedPropertyId}
          propertyRequestStatusId={selectedPropertyRequestStatusId}
          noOfActiveContracts={noOfActiveContracts}
        />
      )}
    </div>
  );
};

export default AssignedBuildingsGrid;
